<template>
  <div class="location-progress-pie">
    <div id="location-progress-pie-chart">
      <apexchart
        v-if="hasData"
        type="donut"
        :height="220"
        :options="chartOptions"
        :series="series"
      />
      <div v-else class="empty-state text-center">No mentee data available</div>
    </div>
    <div v-if="hasData" class="legend">
      <div
        v-for="bucket in bucketed"
        :key="bucket.name"
        class="legend-item"
      >
        <span class="swatch" :style="{ backgroundColor: bucket.color }" />
        <span class="legend-text">
          {{ bucket.name }}:
          <b>{{ bucket.value }} location{{ bucket.value !== 1 ? 's' : '' }}</b>
        </span>
      </div>
    </div>
    <portal to="chart-tooltip-root">
      <div
        v-if="tooltip.show"
        class="location-tooltip"
        :style="{ left: tooltip.left + 'px', top: tooltip.top + 'px' }"
      >
        <div v-if="tooltip.locations.length == 1" class="title" style="color: #002F42; font-weight: bold; font-size: 14px;"><span class="tooltip-swatch" :style="{ backgroundColor: tooltip.color }" /> {{ tooltip.bucketName }} - {{ tooltip.locations.length }} mentee</div>
        <div v-else class="title" style="color: #002F42; font-weight: bold;"><span class="tooltip-swatch" :style="{ backgroundColor: tooltip.color }" /> {{ tooltip.bucketName }} - {{ tooltip.locations.length }} mentees</div>
        <div v-for="loc in tooltip.locations" :key="loc.name" class="row" style="padding-left: 15px; padding-right: 15px;">
          <span class="name">{{ truncate(loc.name) }}</span>
          <span class="pct"><b>{{ loc.pct }}%</b></span>
        </div>
      </div>
    </portal>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

const PROGRESS_THRESHOLDS = {
  red:    '#EE4200',
  yellow: '#f4d454',
  green:  '#009118',
};
const TOOLTIP_W = 240;
const ROW_H     = 20;
const HEADER_H  = 44;

const BUCKETS = [
  { name: '0% – 32%',   color: '#EF4444', match: pct => pct <= 32 },
  { name: '33% – 65%',  color: '#f4d454', match: pct => pct >= 33 && pct <= 65 },
  { name: '66% – 100%', color: '#009118', match: pct => pct >= 66 },
];
const BUCKETSVALUES = [
  { name: '0% – 32%',   color: '#EF4444', match: pct => pct <= 32 },
  { name: '33% – 65%',  color: '#f4d454', match: pct => pct >= 33 && pct <= 65 },
  { name: '66% – 100%', color: '#009118', match: pct => pct >= 66 },
];

export default {
  props: {
    menteeNames: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      tooltip: {
        show: false, left: 0, top: 0,
        bucketName: '', color: '', locations: [],
      },
      _activeBucket: null,
    };
  },
  methods: {
    truncate(text) {
      if(text.length > 25) {
        return  text.substring(0, 25) + '...';
      } else {
        return  text;
      }
    },
    onSliceEnter(event, _ctx, { dataPointIndex }) {
      const bucket = this.bucketed[dataPointIndex];
      if (!bucket) return;
      this.tooltip.bucketName = BUCKETSVALUES[dataPointIndex].name;
      this.tooltip.color      = BUCKETSVALUES[dataPointIndex].color;
      this.tooltip.locations  = bucket.locations;
      this.tooltip.show       = true;
      this.positionTooltip(event);
    },
    onMouseMove(event) {
      if (this.tooltip.show) this.positionTooltip(event);
    },
    onSliceLeave() {
      this.tooltip.show = false;
    },
    positionTooltip(event) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const cursorX = event.clientX;
      const cursorY = event.clientY;
      const estHeight = HEADER_H + this.tooltip.locations.length * ROW_H;
      let left = cursorX + 14;
      let top  = cursorY + 14;
      if (left + TOOLTIP_W > vw - 8) left = cursorX - TOOLTIP_W - 14;
      left = Math.max(8, Math.min(left, vw - TOOLTIP_W - 8));
      if (top + estHeight > vh - 8) top = Math.max(8, vh - Math.min(estHeight, vh - 16) - 8);
      this.tooltip.left = left;
      this.tooltip.top  = top;
    },
  },
  computed: {
    bucketed() {
      const red = [], yellow = [], green = [];
      (this.menteeNames || []).forEach((lp) => {
        const entry = { name: lp.menteeName, pct: lp.avgProgressWithoutPercent };
        if (lp.avgProgressWithoutPercent < 33)      red.push(entry);
        else if (lp.avgProgressWithoutPercent < 66) yellow.push(entry);
        else                          green.push(entry);
      });
      return [
        { name: '0% – 32%',   value: red.length,    color: PROGRESS_THRESHOLDS.red,    locations: red },
        { name: '33% – 65%',  value: yellow.length, color: PROGRESS_THRESHOLDS.yellow, locations: yellow },
        { name: '66% – 100%', value: green.length,  color: PROGRESS_THRESHOLDS.green,  locations: green },
      ].filter((d) => d.value > 0);
    },
    bucketedColors() {
      return BUCKETSVALUES;
    },
    series() {
      return this.bucketed.map(b => b.locations.length);
    },
    hasData() {
      return this.series.length > 0 && this.series.some(v => v > 0);
    },
    chartOptions() {
      return {
        chart: {
          type: 'donut',
          animations: { enabled: false },
          events: {
            dataPointMouseEnter: this.onSliceEnter,
            dataPointMouseLeave: this.onSliceLeave,
            mouseMove:           this.onMouseMove,
          },
        },
        // 3. Always provide a label/color for every slice in `series`
        labels: this.bucketed.map(b => String(b.name)),
        colors: this.bucketed.map(b => b.color),
        legend: { show: false },
        dataLabels: {
          enabled: true,
          offset: 28,
          style: {
            fontSize: '18px',
            fontWeight: 400,
            colors: ['#374151'],
          },
          dropShadow: {
            enabled: false,
          },
          formatter: (val, opts) => {
            const idx = opts && opts.seriesIndex;
            const count = (this.series && this.series[idx]) || 0;
            return count > 0 ? String(count) : '';
          },
        },
        tooltip: { enabled: false },
        fill: {
          opacity: 1
        },
        states: {
          hover: {
            filter: {
              type: 'none'
            }
          },
          active: {
            filter: {
              type: 'none'
            }
          }
        },
        // 5. Plot options hardening for donut
        plotOptions: {
          pie: {
            customScale: 0.8,
            expandOnClick: false,
            dataLabels: {
              offset: 32,
              minAngleToShowLabel: 10,
            },
            donut: {
              size: '65%',
            },
          },
        },
        // 6. Stroke fix — some versions need this when slice count varies
        stroke: { width: 0 },
      };
    },
  },
  mounted() {
    this._onWindowMove = (e) => {
      if (this.tooltip.show) this.positionTooltip(e);
    };
    window.addEventListener('mousemove', this._onWindowMove, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this._onWindowMove);
  },
  components: {
    apexchart: VueApexCharts,
  },
};
</script>

<style scoped>
.location-progress-pie {
  padding-top: 6px;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #1f2937;
}

.legend-item span {
  font-size: 13px;
}

.legend-swatch {
  width: 12px;
  height: 12px;
  margin-right: 10px;
  border-radius: 50%;
  display: inline-block;
}

.swatch-low {
  background: #EE4200;
}

.swatch-mid {
  background: #f4d454;
}

.swatch-high {
  background: #009118;
}

@media (max-width: 1200px) {
  .legend-item,
  .legend-item span {
    font-size: 12px;
  }
}
.location-progress-chart .apexcharts-tooltip,
.location-progress-chart .apexcharts-tooltip-title {
  display: none !important;
}
/* Our portaled tooltip — lives at body level so card overflow can't clip it */
.location-tooltip {
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  max-width: 380px;
  max-height: calc(100vh - 16px);
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 12px;
}
.location-tooltip .title { font-weight: 600; font-size: 13px; margin-bottom: 6px; }
.location-tooltip .row   { display: flex; justify-content: space-between;
                           gap: 12px; padding: 1px 0; color: #374151; }
.location-tooltip .row .pct { font-weight: 600; }
.legend {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: center;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.legend-item .swatch, .tooltip-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.tooltip-swatch {
  border-radius: 0;
}
.legend-item .legend-text {
  font-size: 13px;
  color: #374151;
}

</style>



// WEBPACK FOOTER //
// src/components/manager/pathway_progress_piechart.vue